/**
 * Entity.App — App entity with controller binding (ESM, ha/440).
 *
 * Conversion namespace→ESM. Changement sémantique CRUCIAL (le cœur ha/440) :
 * la résolution du contrôleur passait par `createService(controllerNamespace,
 * controllerName, cdata)` qui faisait `new (namespace)[name](cdata)` — du
 * dispatch par réflexion sur un objet-namespace global. ESM n'ayant pas ces
 * objets, on passe par **`ControllerRegistry.create(controllerName, cdata)`** :
 * dispatch typé `name → ctor`, registre peuplé par les call-sites au top-level
 * de leurs modules respectifs (Page/Menu/Navigation/Service/…).
 *
 * Sémantique observable IDENTIQUE : pour chaque `controllerName` enregistré,
 * `new ctor(cdata)` est appelé exactement comme avant. La différence ne se voit
 * qu'en cas de nom NON enregistré : ancien comportement = `TypeError: Cannot
 * read properties of undefined (reading 'apply')` opaque ; nouveau = `Error:
 * ControllerRegistry: no controller registered for "X"` explicite (gain). Le
 * test ha/440 prouve l'instanciation par registre sur un contrôleur fictif.
 *
 * Le param legacy `controllerNamespace` (lu du JSON SSR) est désormais ignoré :
 * le registre est plat (name → ctor unique). ha/441 retirera les call-sites
 * qui le passent encore.
 */

import { Entity } from "./Entity";
import { Tag } from "../HTML/Tag";
import { ControllerRegistry } from "../Registry/ControllerRegistry";
import { toMap, timeout, log } from "../../../HA";

export class App extends Entity {

    /**
     * ha/508 — Clé d'instance pour AppStack / suivi WebPage. Le `_id` (composite
     * `<pageId>::<params>` posé par Anchor pour les navs paramétrées) prime sur
     * l'`id` (pageId) afin que deux records d'une même page détail soient des
     * instances DISTINCTES. Absent → `id` : comportement historique inchangé.
     */
    public getStackKey(): string {
        return this.get("_id") || this.get("id");
    }

    public setLocalId(id: string): void {
        this.addToData("lid", id);
    }

    public getLocalId(): string {
        if (!this.has("lid")) {
            this.setLocalId(Entity.genId());
        }
        return this.get("lid");
    }

    public setPosition(position: number): void {
        this.addToData("_y", position);
    }

    public getPosition(): number {
        return this.get("_y") || 0;
    }

    private savePosition(): void {
        this.addToData("_y", window.scrollY);
    }

    public isNotCachable(): boolean {
        return !this.isCachable();
    }

    public isCachable(): boolean {
        return this.get("cachable");
    }

    public isViewed(): boolean {
        return this.get("viewed");
    }

    public setViewed(val: boolean): void {
        this.addToData("viewed", val);
    }

    public isNotNavigable(): boolean {
        return !this.isNavigable();
    }

    public isNavigable(): boolean {
        return this.get("navigable");
    }

    public hasUrl(): boolean {
        return this.get("url") !== undefined;
    }

    public getUrl(): any {
        return this.get("url");
    }

    public getName(): any {
        return this.get("appTitle");
    }

    /**
     * Bloc `head` du payload de page (ha/443a) : { title, description,
     * canonical, robots, og:{...}, twitter:{...} }. Produit côté serveur par
     * MockupPageDocument::buildHead et transporté tel quel (pass-through du
     * page model). `toMap` étant peu profond, les sous-objets `og`/`twitter`
     * restent des objets natifs — PageHead.applyHead les lit défensivement.
     * Absent sur les pages non encore enrichies (retour `undefined`).
     */
    public getHead(): any {
        return this.get("head");
    }

    /**
     * CSS compilé de la page (navigation SPA — todo/ha/spa-page-css-injection.md).
     * Produit côté serveur par PageCssService::attach (thèmes scopés
     * `[data-ha-page="<pageId>"]` + globaux, mêmes entrées que le <style> inline
     * du full-load). Absent sur le chemin full-load (CSS déjà dans le head SSR)
     * et sur les payloads non-page. Voyage avec l'entité → réappliqué sur cache hit.
     */
    public getPageCss(): string {
        const css = this.get("pageCss");
        return typeof css === "string" ? css : "";
    }

    public getInfo(): Map<string, any> {
        const tag = this.getTag() as Tag | undefined;
        const d = tag?.getAttributes ? tag.getAttributes(["appname", "apptitle"]) : null;
        if (d && d.get("appname")) {
            return toMap({ appName: d.get("appname"), appTitle: d.get("apptitle") }) as Map<string, any>;
        }
        return toMap({ appTitle: this.get("appTitle"), appName: this.get("appName") }) as Map<string, any>;
    }

    public getQMenu(): Map<any, any> {
        let qm = this.get("qmenu");
        if (qm instanceof Map) return qm;
        qm = toMap(this.get("qmenu"));
        this.addToData("qmenu", qm);
        return qm;
    }

    protected setController(controller: any): Map<string, any> {
        return this.addToData("controller", controller);
    }

    protected getController(): any {
        return this.get("controller");
    }

    protected initialize(data: any): void {
        super.initialize(data);
        if (!this.has("cachable")) {
            this.addToData("cachable", true);
        }
        if (!this.has("navigable")) {
            this.addToData("navigable", true);
        }
        if (this.has("fid")) {
            this.addToData("id", this.get("id") + this.get("fid"));
        }
        this.createTag(data);

        const controllerName = this.get("controllerName");
        if (controllerName) {
            const cdata = toMap(this.getData()) as Map<string, any>;
            // Dispatch par registre (ha/440) — remplace `createService(ns, name, cdata)`.
            // Le param legacy `controllerNamespace` du JSON est ignoré (registre plat).
            this.setController(ControllerRegistry.create(controllerName, cdata));
        }
        if (this.has("msg")) {
            log(this.get("msg"));
        }

        this.intro();
    }

    public outro(): void {
        this.savePosition();
    }

    public intro(): void {
        timeout(() => {
            // Garde : le timer peut fire hors contexte navigateur (teardown jsdom
            // en test, SSR) — `window`/`scrollTo` peuvent être absents.
            if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
                window.scrollTo(0, this.getPosition());
            }
        }, 100);
    }

    protected createTag(data: any): void {
        this.addToData("tag", new Tag(data));
    }
}
